import { useMemo, useState } from "react";
import { FaHouse } from "react-icons/fa6";
import { Link, useLocation } from "react-router-dom";
import { useNavOptional } from "../../../context/NavContext";
import { useContractPath } from "../../../hooks/useContractPath";
import { isNavItemLocked } from "../../../api/scaNav";
import { appPathname } from "../../../utils/contractPath";
import { CompanySwitcher } from "./CompanySwitcher";
import { DriveConnectedBadge } from "./DriveConnectedBadge";
import { UserSwitcher } from "./UserSwitcher";
import { APP_DISPLAY_NAME } from "../../../constants/appBranding";
import { EnvBadge } from "../EnvBadge/EnvBadge";
import "./AppHeaderBar.css";

/**
 * Barra superior comum: home, título, seletor de empresa e menu do usuário.
 * `extraBeforeTitle` / `extraAfterTitle`: nós adicionais na faixa esquerda (ex.: segundo link, botões).
 * Favorito (★): automático pela rota atual quando o item de menu permite favoritar.
 */
export function AppHeaderBar({
  title,
  showBack = true,
  backTo = "/inicio",
  backLabel = null,
  onBackClick,
  headerClassName = "usuarios-top",
  extraBeforeTitle = null,
  extraAfterTitle = null,
  /** false desliga a ★ (ex.: Home). Default: mostrar se houver menu correspondente. */
  showFavorite = true,
  /** { back, title, company, logout } — ordem de tab no formulário Mov. sócios */
  formTabIndices = null,
  /** { inicio, movSocios, company, logout } — refs para foco programático */
  headerFocusRefs = null,
  titleAsLink = false,
  onTitleLinkClick,
  onLogoutKeyDown,
  onHeaderInicioKeyDown,
  onHeaderMovSociosKeyDown,
  onHeaderCompanyKeyDown,
}) {
  const location = useLocation();
  const cp = useContractPath();
  const nav = useNavOptional();
  const [favError, setFavError] = useState("");
  const resolvedBackTo = cp(backTo);

  const menuItem = useMemo(() => {
    if (!showFavorite || !nav?.findMenuItemForPath) return null;
    return nav.findMenuItemForPath(
      appPathname(location.pathname),
      location.search
    );
  }, [showFavorite, nav, location.pathname, location.search]);

  const canFavorite =
    Boolean(menuItem?.fl_allow_favorite) &&
    menuItem != null &&
    !isNavItemLocked(menuItem);

  const onToggleFavorite = async () => {
    if (!canFavorite || !nav?.toggleFavorite) return;
    setFavError("");
    try {
      await nav.toggleFavorite(menuItem);
    } catch (e) {
      setFavError(
        e instanceof Error ? e.message : "Falha ao atualizar favorito."
      );
    }
  };

  const brandInner = (
    <>
      <FaHouse aria-hidden className="app-header-bar__home-icon" />
      <span className="app-header-bar__system-name">{APP_DISPLAY_NAME}</span>
    </>
  );

  return (
    <header className={`app-header-bar ${headerClassName}`.trim()}>
      <div className="app-header-bar__left">
        {showBack ? (
          <Link
            ref={headerFocusRefs?.inicio}
            className={
              backLabel
                ? "usuarios-back"
                : "usuarios-back app-header-bar__home app-header-bar__brand"
            }
            to={resolvedBackTo}
            onClick={onBackClick}
            tabIndex={formTabIndices?.back}
            onKeyDown={onHeaderInicioKeyDown}
            title={backLabel ? undefined : "Página principal"}
            aria-label={backLabel || `Página principal — ${APP_DISPLAY_NAME}`}
          >
            {backLabel ? backLabel : brandInner}
          </Link>
        ) : (
          <span
            className="app-header-bar__home app-header-bar__brand"
            aria-label={APP_DISPLAY_NAME}
          >
            {brandInner}
          </span>
        )}
        <EnvBadge className="app-header-bar__env" />
        {extraBeforeTitle}
        {title && titleAsLink ? (
          <Link
            ref={headerFocusRefs?.movSocios}
            className="app-header-bar__module-link"
            to={cp("/gsf/movimentacao-socios")}
            onClick={(e) => {
              if (onTitleLinkClick) {
                e.preventDefault();
                onTitleLinkClick();
              }
            }}
            tabIndex={formTabIndices?.title}
            onKeyDown={onHeaderMovSociosKeyDown}
          >
            {title}
          </Link>
        ) : title ? (
          <h1>{title}</h1>
        ) : null}
        {canFavorite ? (
          <button
            type="button"
            className={
              menuItem.fl_favorite
                ? "app-header-bar__fav app-header-bar__fav--on"
                : "app-header-bar__fav"
            }
            title={
              menuItem.fl_favorite
                ? "Remover dos favoritos"
                : "Adicionar aos favoritos"
            }
            aria-label={
              menuItem.fl_favorite
                ? `Remover ${menuItem.nm_menu} dos favoritos`
                : `Favoritar ${menuItem.nm_menu}`
            }
            aria-pressed={Boolean(menuItem.fl_favorite)}
            disabled={nav?.favBusy === menuItem.cd_menu}
            onClick={() => void onToggleFavorite()}
          >
            {menuItem.fl_favorite ? "★" : "☆"}
          </button>
        ) : null}
        {extraAfterTitle}
        {favError ? (
          <span className="app-header-bar__fav-error" role="alert">
            {favError}
          </span>
        ) : null}
      </div>
      <div className="app-header-bar__right">
        <DriveConnectedBadge />
        <CompanySwitcher
          tabIndex={formTabIndices?.company}
          buttonRef={headerFocusRefs?.company}
          onButtonKeyDown={onHeaderCompanyKeyDown}
        />
        <UserSwitcher
          buttonRef={headerFocusRefs?.logout}
          tabIndex={formTabIndices?.logout}
          onButtonKeyDown={onLogoutKeyDown}
        />
      </div>
    </header>
  );
}
